import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ZoomIn, ChevronLeft, ChevronRight } from "lucide-react";
import { BlurImage } from "@/components/BlurImage";
import axios from "axios";

const API = `/api`;

const fallbackImages = [
  { id: "1", src: "/images/galeria-1.webp", alt: "Receção Justfit Maia", category: "Receção" },
  { id: "2", src: "/images/galeria-2.webp", alt: "Estúdio RPM/Cycling", category: "Aulas" },
  { id: "3", src: "/images/galeria-3.webp", alt: "Estúdio Aeropilates", category: "Aulas" },
  { id: "4", src: "/images/galeria-4.webp", alt: "Sala de Musculação Maia", category: "Equipamentos" },
  { id: "5", src: "/images/galeria-5.webp", alt: "Receção Justfit Barcelos", category: "Receção" },
  { id: "6", src: "/images/galeria-6.webp", alt: "Estúdio de Aulas Barcelos", category: "Aulas" },
];

export const Gallery = () => {
  const [images, setImages] = useState(fallbackImages);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [direction, setDirection] = useState(0);
  const [touchStart, setTouchStart] = useState(null);

  useEffect(() => {
    axios.get(`${API}/content/gallery?club=homepage`)
      .then((res) => {
        if (res.data && res.data.length > 0) {
          setImages(res.data.filter((img) => img.active === "1" || img.active === true));
        }
      })
      .catch(() => {});
  }, []);

  const goNext = useCallback(() => {
    if (selectedIndex < images.length - 1) {
      setDirection(1);
      setSelectedIndex((prev) => prev + 1);
    }
  }, [selectedIndex, images.length]);

  const goPrev = useCallback(() => {
    if (selectedIndex > 0) {
      setDirection(-1);
      setSelectedIndex((prev) => prev - 1);
    }
  }, [selectedIndex]);

  const closeLightbox = useCallback(() => {
    setSelectedIndex(-1);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    if (selectedIndex < 0) return;
    const handleKey = (e) => {
      if (e.key === "ArrowRight") goNext();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [selectedIndex, goNext, goPrev, closeLightbox]);

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (touchStart === null) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (diff > 60) goNext();
    else if (diff < -60) goPrev();
    setTouchStart(null);
  };

  const selectedImage = selectedIndex >= 0 ? images[selectedIndex] : null;

  const variants = {
    enter: (dir) => ({ x: dir > 0 ? 300 : -300, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? -300 : 300, opacity: 0 }),
  };

  return (
    <section id="galeria" className="py-12 lg:py-16 bg-[#FAF8F5]" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 lg:gap-4 auto-rows-[160px] md:auto-rows-[200px]">
          {images.map((image, index) => (
            <motion.div
              key={image.id}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className={`relative overflow-hidden rounded-xl cursor-pointer group ${
                index === 0 ? "md:col-span-2 md:row-span-2" : ""
              }`}
              onClick={() => { setDirection(0); setSelectedIndex(index); }}
              data-testid={`gallery-image-${image.id}`}
            >
              <BlurImage
                src={image.src}
                alt={image.alt || `Ginásio Justfit - ${image.category || 'Espaço'}`}
                className="w-full h-full object-cover rounded-xl"
              />
              <div className="absolute inset-0 rounded-xl bg-black/0 group-hover:bg-black/50 transition-colors duration-300 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-center">
                  <ZoomIn size={28} className="text-white mx-auto mb-2" />
                  <p className="text-white font-bold text-sm">{image.alt || ""}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Lightbox with navigation */}
      <AnimatePresence custom={direction}>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={closeLightbox}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            data-testid="gallery-lightbox"
          >
            {/* Close button */}
            <button
              onClick={closeLightbox}
              className="absolute top-6 right-6 z-10 text-white/70 hover:text-white transition-colors"
              data-testid="close-lightbox"
            >
              <X size={32} />
            </button>

            {/* Counter */}
            <div className="absolute top-6 left-6 text-white/50 text-sm font-bold" data-testid="gallery-counter">
              {selectedIndex + 1} / {images.length}
            </div>

            {/* Previous arrow */}
            {selectedIndex > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); goPrev(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                data-testid="lightbox-prev-btn"
              >
                <ChevronLeft size={28} />
              </button>
            )}

            {/* Next arrow */}
            {selectedIndex < images.length - 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); goNext(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                data-testid="lightbox-next-btn"
              >
                <ChevronRight size={28} />
              </button>
            )}

            {/* Image */}
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={selectedIndex}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="max-w-5xl max-h-[80vh] px-16 relative"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={selectedImage.src}
                  alt={selectedImage.alt || "Justfit"}
                  className="max-w-full max-h-[80vh] object-contain rounded-lg mx-auto"
                  draggable={false}
                />
                <div className="absolute bottom-0 left-16 right-16 p-4 bg-gradient-to-t from-black to-transparent rounded-b-lg">
                  <p className="text-white text-lg font-bold">{selectedImage.alt || ""}</p>
                  <span className="text-[#FED601] text-sm">{selectedImage.category || ""}</span>
                </div>
              </motion.div>
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
